import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as LeftArrow } from "../../../../../../assets/arrow-left.svg";
import { ReactComponent as LeftArrowDark } from "../../../../../../assets/arrow-left-dark.svg";
import { MdAdd } from "react-icons/md";

import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-control-geocoder";
import useLocales from "../../../../../../hooks/useLocales";
import useSettings from "../../../../../../hooks/useSettings";
import { Link, useNavigate } from "react-router-dom";
import AddLocationButton from "./AddLocationButton";
import MarkerIcon from "../../../../../../assets/MainPage/addBlock/marker.svg";
import { useStateContext } from "../../../../../../contexts/ThemeContext";
import { useDispatch, useSelector } from "react-redux";
import httpRequest from "../../../../../../axios/index";
import {
  setShowForm,
  setShowLocationData,
} from "../../../../../../redux/slices/location/locationSlice";

import toast from "react-hot-toast";
import AddLocationForm from "../../forms/locationForms/AddLocationForm";
import LocationFormData from "../../forms/locationForms/LocationFormData";
import EditLocationForms from "../../edit/EditLocationForms";

const AddLocation = () => {
  const { darkMode } = useSelector((state) => state.theme);
  const dispatch = useDispatch();
  const { showForm, showLocationData, editingLocation } = useSelector(
    (state) => state.location
  );
  const { userid } = JSON.parse(localStorage.getItem("user"));
  const linkid = localStorage.getItem("linkId");

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const geocoderRef = useRef(null); // Ref for the geocoder control
  const currentMarker = useRef(null);
  const showFormRef = useRef(showForm);
  const { translate, currentLang } = useLocales();
  const { themeDirection } = useSettings();
  const navigate = useNavigate();
  const { setActiveMainPageNav } = useStateContext();

  const [locationMap, setLocationMap] = useState([]);
  const [payload, setPayload] = useState(null);
  const [currentAddress, setCurrentAddress] = useState(""); // State to store the address

  const [selectedLang, setSelectedLang] = useState(currentLang?.value); // Default language is English

  useEffect(() => {
    if (currentLang) {
      setSelectedLang(currentLang?.value);
    }
  }, [currentLang]);

  useEffect(() => {
    showFormRef.current = showForm; // Update the ref whenever showForm changes
  }, [showForm]);

  useEffect(() => {
    getLocationMapData();
  }, []);

  const getLocationMapData = async () => {
    const { userid } = JSON.parse(localStorage.getItem("user"));
    try {
      const url = `api/user/maps/${userid}/${selectedLang}`;
      const response = await httpRequest.get(url);
      const resultSuccess = response?.data?.data;
      const status = response?.data?.status;
      if (status === "success") {
        setLocationMap(resultSuccess);
      }
    } catch (error) {
      if (error?.response?.data?.status === "failed") {
        toast.error(error?.response?.data?.message);
      } else {
        toast.error("Something went wrong");
      }
    }
  };

  useEffect(() => {
    if (mapRef.current && !mapInstance.current) {
      // Initialize the map
      mapInstance.current = L.map(mapRef.current).setView([20, 0], 2); // Set view to show countries
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(mapInstance.current);

      // Remove default zoom control
      mapInstance.current.zoomControl.remove();

      // Add custom zoom control to top right corner
      const zoomControl = L.control.zoom({ position: "topright" });
      mapInstance.current.addControl(zoomControl);

      // Custom marker icon for search results
      const customSearchIcon = L.icon({
        iconUrl: MarkerIcon,
        iconSize: [32, 32], // Adjust as needed
      });

      // Handle geocoder results with a custom marker
      geocoderRef.current = L.Control.geocoder({ position: "topleft" }).addTo(
        mapInstance.current
      );
      geocoderRef.current.on("markgeocode", (e) => {
        const { center, name } = e.geocode;
        // Remove previous search markers
        mapInstance.current.eachLayer((layer) => {
          if (layer instanceof L.Marker && !layer.options.isLocationMapMarker) {
            mapInstance.current.removeLayer(layer);
          }
        });

        // Add custom marker for search result
        const searchMarker = L.marker([center.lat, center.lng], {
          icon: customSearchIcon,
          isLocationMapMarker: false, // Mark this marker as not from locationMap
        }).addTo(mapInstance.current);
        searchMarker
          .bindPopup(`<b>${name}</b><br>${center.lat}, ${center.lng}`)
          .openPopup();
        mapInstance.current.setView([center.lat, center.lng], 15); // Zoom to the search result
      });
    }

    if (mapInstance.current) {
      // Remove existing location markers
      mapInstance.current.eachLayer((layer) => {
        if (layer instanceof L.Marker && layer.options.isLocationMapMarker) {
          mapInstance.current.removeLayer(layer);
        }
      });

      if (locationMap.length > 0) {
        // Custom marker icon
        const customIcon = L.icon({
          iconUrl: MarkerIcon,
          iconSize: [32, 32], // Adjust as needed
        });

        let primaryLocation = null;

        // Add markers for each location
        locationMap.forEach((location) => {
          const { lat, lon, title, address, directionUrl, isPrimary, _id } =
            location;
          const isEditable = editingLocation && _id === editingLocation;

          const marker = L.marker([lat, lon], {
            icon: customIcon,
            draggable: isEditable, // Enable dragging
            isLocationMapMarker: true, // Mark this marker as from locationMap
          }).addTo(mapInstance.current);
          marker.bindPopup(`
            <b>${title}</b><br>
            ${address}<br>
            <a href="${directionUrl}" target="_blank">Get Directions</a>
          `);

          // Zoom to marker location on click
          if (!isEditable) {
            marker.on("click", () => {
              mapInstance.current.setView([lat, lon], 15); // Adjust the zoom level as needed
            });
          }
          // Handle dragend event to update location data
          marker.on("dragend", async (event) => {
            if (isEditable) {
              const { lat, lng } = event.target.getLatLng();
              const updatedLocation = {
                ...location,
                lat,
                lon: lng,
              };

              const directionUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
              updatedLocation.directionUrl = directionUrl;

              // Reverse geocode to get the address based on lat and lng
              try {
                const response = await fetch(
                  `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
                );
                const data = await response.json();
                const address = data.display_name;
                updatedLocation.address = address;

                setPayload({
                  coloum_name: "mapData",
                  coloum_type: "mapData",
                  link_id: linkid,
                  userId: userid,
                  title: updatedLocation.title,
                  address: updatedLocation.address,
                  phone: updatedLocation.phone,
                  lat: updatedLocation.lat,
                  lon: updatedLocation.lon,
                  directionUrl: updatedLocation.directionUrl,
                  isPrimary: updatedLocation.isPrimary,
                  mapId: updatedLocation._id,
                  language: updatedLocation.language,
                });
              } catch (error) {
                toast.error("Error fetching address from geocoding service");
              }
            }
          });

          if (isPrimary) {
            primaryLocation = location;
            // Highlight primary location with a different icon or marker color
            marker.setIcon(
              L.icon({
                iconUrl: MarkerIcon, // Use a different icon for primary marker
                iconSize: [32, 32], // Adjust as needed
              })
            );
          }
        });

        if (primaryLocation) {
          mapInstance.current.setView(
            [primaryLocation.lat, primaryLocation.lon],
            5
          );
        }
      }

      mapInstance.current.on("click", async (event) => {
        if (!showFormRef.current) return; // Prevent adding marker if showForm is false

        const { lat, lng } = event.latlng;

        // Remove the existing marker if present
        if (currentMarker.current) {
          mapInstance.current.removeLayer(currentMarker.current);
        }

        // Create a new marker at the clicked position
        const markerOptions = {
          icon: L.icon({
            iconUrl: MarkerIcon,
            iconSize: [32, 32], // Adjust as needed
          }),
          draggable: showForm, // Make draggable if showForm is true
          isLocationMapMarker: true, // Mark this marker as from locationMap
        };

        const marker = L.marker([lat, lng], markerOptions).addTo(
          mapInstance.current
        );
        currentMarker.current = marker;

        let popupContent = `
          <b>New Pin</b><br>
          Latitude: ${lat}<br>
          Longitude: ${lng}
        `;

        // Fetch address and direction URL
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
          );
          const data = await response.json();
          const address = data.display_name;
          const directionUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

          // Update popup content with address and direction URL
          popupContent = `
            <b>New Pin</b><br>
            ${address}<br>
            <a href="${directionUrl}" target="_blank">Get Directions</a>
          `;

          // Update marker's popup with address and direction URL
          marker.bindPopup(popupContent);
          marker.openPopup(); // Ensure the popup is opened
          setCurrentAddress(address);
        } catch (error) {
          toast.error("Error fetching address from geocoding service");
          marker.bindPopup(popupContent); // Ensure marker still has default popup content in case of error
          marker.openPopup(); // Ensure the popup is opened even if there is an error
        }

        // Handle marker dragend event to update address and popup content
        marker.on("dragend", async (event) => {
          const { lat, lng } = event.target.getLatLng();

          // Update the marker's position
          marker.setLatLng([lat, lng]);

          let updatedPopupContent = `
            <b>New Pin</b><br>
            Latitude: ${lat}<br>
            Longitude: ${lng}
          `;

          // Fetch updated address and direction URL
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
            );
            const data = await response.json();
            const address = data.display_name;
            const directionUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

            // Update popup content with the new address and direction URL
            updatedPopupContent = `
              <b>New Pin</b><br>
              ${address}<br>
              <a href="${directionUrl}" target="_blank">Get Directions</a>
            `;

            // Update marker's popup with the new content
            marker.bindPopup(updatedPopupContent);
            marker.openPopup(); // Ensure the popup is opened
            setCurrentAddress(address);
          } catch (error) {
            toast.error("Error fetching address from geocoding service");
            marker.bindPopup(updatedPopupContent); // Ensure marker still has default popup content in case of error
            marker.openPopup(); // Ensure the popup is opened even if there is an error
          }
        });
      });
    }
  }, [mapRef, locationMap, editingLocation, showForm]);

  useEffect(() => {
    setActiveMainPageNav(true);
    return () => {
      setActiveMainPageNav(false);
    };
  }, [setActiveMainPageNav]);

  // const handleNavigate = () => {
  //   navigate("/dashboard/component/locationForm");
  // };

  const handleNavigate = () => {
    dispatch(setShowForm(true));
    dispatch(setShowLocationData(false));
  };

  const handleAddLocationButtonClick = () => {
    dispatch(setShowForm(true));
    dispatch(setShowLocationData(false));
  };

  return (
    <>
      <div className="md:px-0 mt-10 mb-6 px-6 flex sm:flex-row items-start sm:items-center justify-between">
        <div className="flex items-center gap-2">
          <Link
            to={"/dashboard/component"}
            className="flex text-body_copy2 items-center cursor-pointer"
          >
            <div>
              {darkMode ? (
                <LeftArrowDark
                  className={`${
                    themeDirection === "rtl" ? "transform -scale-x-100" : ""
                  }`}
                />
              ) : (
                <LeftArrow
                  className={`${
                    themeDirection === "rtl" ? "transform -scale-x-100" : ""
                  }`}
                />
              )}
            </div>
            <p className="dark:text-color_v1_dark">
              {translate("cms.location")}
            </p>
          </Link>
        </div>
        {showLocationData && (
          <button
            onClick={() => handleAddLocationButtonClick()}
            className="rounded-full cursor-pointer flex gap-2 lg:gap-[12.5px] p-2 lg:pt-[9px] px-4 lg:px-[26px] text-white bg-main_color lg:text-small2_b text-small3_b"
          >
            <div className="text-gray-50 -pt-1 lg:text-xl text-lg">
              <MdAdd />
            </div>
            {translate("location.add_location")}
          </button>
        )}
      </div>

      <div className="sm:mt-7 md:px-0 mt-10 mb-6 px-6">
        <div className="map-container">
          <div
            ref={mapRef}
            className="h-96 rounded-lg overflow-hidden shadow-lg"
            style={{ zIndex: "1" }}
          />
        </div>
        {!showForm && !showLocationData && !editingLocation && (
          <AddLocationButton onClick={handleNavigate} />
        )}
        {showForm && (
          <AddLocationForm
            getLocationMapData={getLocationMapData}
            currentAddress={currentAddress}
            setCurrentAddress={setCurrentAddress}
          />
        )}
        {showLocationData && (
          <LocationFormData
            locationMap={locationMap}
            getLocationMapData={getLocationMapData}
          />
        )}
        {editingLocation && (
          <EditLocationForms
            getLocationMapData={getLocationMapData}
            payloadPin={payload}
            setPayload={setPayload}
          />
        )}
      </div>
    </>
  );
};

export default AddLocation;
